import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AdminRelatedLinks,
  AdminBlogShell,
  AdminListPagination,
  AdminListToolbar,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { fetchYowanAdminFacilityList } from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const FACILITY_PAGE_SIZE = 30;
const facilitySortOptions = [
  { value: 'active-status', label: '使用状態順' },
  { value: 'name-asc', label: '施設名昇順' },
  { value: 'name-desc', label: '施設名降順' },
  { value: 'updated-desc', label: '更新日が新しい順' },
];

const facilityColumnStyle = {
  width: '18rem',
  minWidth: '18rem',
};

const addressColumnStyle = {
  width: '18rem',
  minWidth: '18rem',
};

const compactColumnStyle = {
  width: '6rem',
  minWidth: '6rem',
};

const updatedAtColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
  whiteSpace: 'nowrap',
};

const actionColumnStyle = {
  width: '9rem',
  minWidth: '9rem',
  whiteSpace: 'nowrap',
};

const compareText = (left = '', right = '') => String(left || '').localeCompare(String(right || ''), 'ja');

const comparableDateTime = (value = '') => {
  const timestamp = Date.parse(String(value || '').replace(' ', 'T'));
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortFacilityRows = (rows, sortKey) => {
  const nextRows = [...rows];

  nextRows.sort((left, right) => {
    if (sortKey === 'active-status') {
      if (left.isActive === right.isActive) {
        return compareText(left.facilityName, right.facilityName);
      }
      return left.isActive ? -1 : 1;
    }

    if (sortKey === 'name-asc') {
      return compareText(left.facilityName, right.facilityName);
    }

    if (sortKey === 'name-desc') {
      return compareText(right.facilityName, left.facilityName);
    }

    if (sortKey === 'updated-desc') {
      return comparableDateTime(right.updatedAt) - comparableDateTime(left.updatedAt);
    }

    return compareText(left.facilityName, right.facilityName);
  });

  return nextRows;
};

const statusToUi = (isActive) => ({
  label: isActive ? '使用中' : '停止中',
  tone: isActive ? 'published' : 'draft',
});

const buildRoomsHref = (row = {}) => {
  const searchParams = new URLSearchParams();

  if (row.id) {
    searchParams.set('facilityId', row.id);
  }

  if (row.facilityName) {
    searchParams.set('facilityLabel', row.facilityName);
  }

  const query = searchParams.toString();

  return query ? `/admin/events/rooms?${query}` : '/admin/events/rooms';
};

const AdminFacilitiesListPage = () => {
  const content = yowanPageContent['/admin/events/facilities'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState('name-asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [apiCount, setApiCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    if (!content) {
      return undefined;
    }

    let isMounted = true;

    const loadFacilities = async () => {
      setIsLoading(true);
      setLoadError('');

      try {
        const payload = await fetchYowanAdminFacilityList({
          page: currentPage,
          pageSize: FACILITY_PAGE_SIZE,
          q: searchQuery.trim(),
        });

        if (!isMounted) {
          return;
        }

        setRows(payload.results);
        setApiCount(payload.count);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setRows([]);
        setApiCount(0);
        setLoadError('施設情報を読み込めませんでした。');
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadFacilities();

    return () => {
      isMounted = false;
    };
  }, [content, currentPage, searchQuery]);

  if (!content) {
    return null;
  }

  const sortedRows = sortFacilityRows(rows, sortKey);
  const totalPages = Math.max(1, Math.ceil(apiCount / FACILITY_PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const startIndex = (safeCurrentPage - 1) * FACILITY_PAGE_SIZE;
  const visibleRows = sortedRows;

  return (
    <AdminBlogShell activeNavHref="/admin/events/facilities" routeKey="/admin/events/facilities">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link
            key="primary-action"
            className="c-form__submit"
            to={appendYowanPreviewQueryIfNeeded(content.primaryAction.href, preservedDomainSearch)}
          >
            {content.primaryAction.label}
          </Link>,
        ]}
      />

      <AdminListToolbar
        searchValue={searchQuery}
        onSearchChange={(event) => {
          setSearchQuery(event.target.value);
          setCurrentPage(1);
        }}
        sortValue={sortKey}
        onSortChange={(event) => {
          setSortKey(event.target.value);
          setCurrentPage(1);
        }}
        metaText={`${apiCount}件中 ${visibleRows.length}件を表示${
          totalPages > 1 ? ` / ${safeCurrentPage} / ${totalPages}ページ` : ''
        }`}
        searchPlaceholder="施設名 / 略称 / 住所 / 電話 / 使用状態"
        sortOptions={facilitySortOptions}
      />
      <AdminRelatedLinks links={content.relatedLinks} preservedDomainSearch={preservedDomainSearch} />

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table">
          <thead>
            <tr>
              <th scope="col">#</th>
              <th scope="col" style={facilityColumnStyle}>施設</th>
              <th scope="col" style={addressColumnStyle}>住所</th>
              <th scope="col" style={compactColumnStyle}>部屋数</th>
              <th scope="col">使用状態</th>
              <th scope="col" style={updatedAtColumnStyle}>更新日</th>
              <th scope="col" style={actionColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((row, index) => {
              const statusUi = statusToUi(row.isActive);

              return (
                <tr key={row.id}>
                  <td>{startIndex + index + 1}</td>
                  <td style={facilityColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.facilityName}</p>
                    <p className="yowan-admin-resource-table__subline">{row.shortName || '-'}</p>
                  </td>
                  <td style={addressColumnStyle}>{row.address || '-'}</td>
                  <td style={compactColumnStyle}>{row.roomCount}</td>
                  <td>
                    <span className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${statusUi.tone}`}>
                      {statusUi.label}
                    </span>
                  </td>
                  <td style={updatedAtColumnStyle}>{row.updatedAt || '-'}</td>
                  <td style={actionColumnStyle}>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={appendYowanPreviewQueryIfNeeded(row.editHref, preservedDomainSearch)}>編集</Link>
                      <Link to={appendYowanPreviewQueryIfNeeded(buildRoomsHref(row), preservedDomainSearch)}>部屋</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!isLoading && visibleRows.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  {loadError || '表示できる施設がありません。'}
                </td>
              </tr>
            ) : null}
            {isLoading ? (
              <tr>
                <td colSpan={7}>
                  <LoadingSpinner size="sm" />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>

      <AdminListPagination
        currentPage={safeCurrentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
      />
    </AdminBlogShell>
  );
};

export default AdminFacilitiesListPage;
